/**
 * Misura e scala condivise dai grafici SVG (`ValueTrendChart`, `PortfolioValueChart`).
 * File a parte perché un modulo di componenti che esporta anche funzioni
 * rompe il fast refresh di Vite.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Larghezza di ripiego quando la misura non è disponibile: primo render prima
 * che il layout esista, e ambienti senza `ResizeObserver` (jsdom). Non è un
 * valore "giusto", è un valore che non rompe nulla finché arriva quello vero.
 */
const FALLBACK_WIDTH = 720;

/** Scala arrotondata "gentile": estremi leggibili invece che dettati dai dati. */
export function niceBounds(min: number, max: number): { lower: number; upper: number } {
  if (min === max) {
    // Un solo livello di valore: si apre una banda attorno, altrimenti la
    // linea finirebbe schiacciata sul bordo del riquadro.
    const padding = Math.abs(min) * 0.1 || 1;
    return { lower: min - padding, upper: max + padding };
  }

  const span = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(span))) / 2;
  return { lower: Math.floor(min / step) * step, upper: Math.ceil(max / step) * step };
}

/**
 * La larghezza resa dell'elemento, per far coincidere unità SVG e pixel.
 *
 * Senza `ResizeObserver` — jsdom, browser antichi — resta il ripiego: il
 * grafico si disegna comunque, solo con una scala che non segue il contenitore.
 * Un grafico un po' meno preciso è meglio di un grafico assente.
 */
export function useMeasuredWidth(): [React.RefObject<HTMLElement | null>, number] {
  const ref = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      // Sotto i 240px non si disegna niente di leggibile: meglio tenere una
      // larghezza minima e lasciare scorrere il contenitore.
      if (measured > 0) setWidth(Math.max(240, Math.round(measured)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

